import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LiveChatStateService } from 'src/app/modules/live-chat/services/live-chat-state.service';

export type PhoneToggleStatus =
  | 'offline'
  | 'online'
  | 'pause'
  | 'ringing'
  | 'talking'
  | 'loading'
  | 'other_tab_online'
  | 'other_tab_busy';

export interface OtherTabPhoneState {
  phoneStatus: PhoneToggleStatus;
  timestamp: number;
  force?: boolean
}

const STORAGE_KEY = 'phone_status_sync';

@Injectable({
  providedIn: 'root',
})
export class PhoneStateSyncService implements OnDestroy {
  private readonly otherTabState$ = new BehaviorSubject<OtherTabPhoneState | null>(null);
  private storageSubscription: Subscription | null = null;
  private beforeUnloadHandler: () => void;
  private currentTabStatus: PhoneToggleStatus = 'offline';

  constructor() {
    this.beforeUnloadHandler = this.onBeforeUnload.bind(this);
    this.initStorageListener();
    this.initBeforeUnloadListener();
    this.readInitialState();
  }

  get otherTabState(): Observable<OtherTabPhoneState | null> {
    return this.otherTabState$.asObservable();
  }

  get currentOtherTabState(): OtherTabPhoneState | null {
    return this.otherTabState$.getValue();
  }

  ngOnDestroy(): void {
    this.storageSubscription?.unsubscribe();
    window.removeEventListener('unload', this.beforeUnloadHandler);
  }

 updateStatus(status: PhoneToggleStatus, canForce = false): void {
    let force = false;
    this.currentTabStatus = status;
    if (
      status === 'offline' &&
      this.currentOtherTabState?.phoneStatus &&
      this.currentOtherTabState?.phoneStatus !== 'offline' &&
      canForce
    ) {
      status = this.currentOtherTabState!.phoneStatus;
      force = true;
    }
    const state: OtherTabPhoneState = {
      phoneStatus: status,
      timestamp: Date.now(),
    };

    this.writeToStorage(state);
    this.otherTabState$.next({ ...state, force });
  }

  clearStatus(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.otherTabState$.next(null);
  }

  private initStorageListener(): void {
    this.storageSubscription = fromEvent<StorageEvent>(window, 'storage')
      .pipe(filter((event) => event.key === STORAGE_KEY))
      .subscribe((event) => {
        this.handleStorageChange(event);
      });
  }

  private initBeforeUnloadListener(): void {
    window.addEventListener('unload', this.beforeUnloadHandler);
  }

  private onBeforeUnload(): void {
    const canSetOffline = 
      this.currentTabStatus === 'online' ||
      this.currentTabStatus === 'ringing' ||
      this.currentTabStatus === 'talking';

    if (!canSetOffline) {
      return;
    }

    const state: OtherTabPhoneState = {
      phoneStatus: 'offline',
      timestamp: Date.now(),
    };
    this.writeToStorage(state);
  }

  private readInitialState(): void {
    const storedValue = localStorage.getItem(STORAGE_KEY);
    if (storedValue) {
      const state = this.parseState(storedValue);
      if (state) {
        this.otherTabState$.next(state);
      }
    }
  }

  private handleStorageChange(event: StorageEvent): void {
    if (event.newValue === null) {
      this.otherTabState$.next(null);
      return;
    }

    const state = this.parseState(event.newValue);
    if (state) {
      this.otherTabState$.next(state);
    }
  }

  private writeToStorage(state: OtherTabPhoneState): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  private parseState(value: string): OtherTabPhoneState | null {
    try {
      const parsed = JSON.parse(value) as OtherTabPhoneState;
      if (this.isValidState(parsed)) {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  }

  private isValidState(state: unknown): state is OtherTabPhoneState {
    if (typeof state !== 'object' || state === null) {
      return false;
    }
    const s = state as Record<string, unknown>;
    return (
      typeof s['phoneStatus'] === 'string' &&
      typeof s['timestamp'] === 'number'
    );
  }
}
